/**
 * Single source of truth for "where should we send the user once they finish
 * authenticating?".
 *
 * Historically this intent was scattered across three uncoordinated channels
 * (`location.state.from`, `localStorage.intendedRoute`,
 * `localStorage.redirectAfterLogin`) read by different code paths with
 * different fallbacks — which is why a login could silently dump the user on
 * `/insights` instead of the page they were trying to reach. Everything now
 * funnels through `setPostAuthRedirect` / `consumePostAuthRedirect`.
 *
 * Legacy keys are still *read* (and cleared) on consume so any in-flight write
 * from older call sites keeps working during rollout.
 */

const KEY = 'postAuthRedirect';
const LEGACY_KEYS = ['intendedRoute', 'redirectAfterLogin'] as const;

/**
 * Only ever redirect to a same-origin, non-auth path. Rejects:
 *  - external / protocol-relative URLs (`//evil.com`, `https://…`)
 *  - the auth surfaces themselves (prevents "log in to log in" loops)
 *  - anything that isn't an absolute in-app path
 */
export const isSafeInternalPath = (p: unknown): p is string =>
  typeof p === 'string' &&
  p.startsWith('/') &&
  !p.startsWith('//') &&
  !/^\/(login|signup|forgot-password|reset-password|auth)(\/|$|\?)/.test(p);

/** Record where the user was headed before we interrupted them for auth. */
export const setPostAuthRedirect = (path: string | null | undefined): void => {
  if (isSafeInternalPath(path) && path !== '/') {
    localStorage.setItem(KEY, path);
  }
};

/**
 * The default post-auth destination: keep the user exactly where they are.
 * Auth happens in-place (a modal over the current page), so the sensible
 * fallback when no explicit intent was stored is the current path — never a
 * forced bounce to home. Returns `home` only when the current path is itself
 * an auth surface (or otherwise unsafe), where staying put makes no sense.
 */
export const stayHereOr = (currentPath?: string | null, home = '/'): string =>
  isSafeInternalPath(currentPath) ? (currentPath as string) : home;

/**
 * Resolve *and consume* the post-auth destination. Reads the canonical key
 * first, then legacy keys, then the optional router `from` pathname, clearing
 * all of them so a stale intent can never misroute a later, unrelated login.
 * Returns `fallback` when nothing valid was stored.
 */
export const consumePostAuthRedirect = (
  fromState?: string | null,
  fallback = '/',
): string => {
  const candidates: unknown[] = [
    localStorage.getItem(KEY),
    ...LEGACY_KEYS.map((k) => localStorage.getItem(k)),
    fromState,
  ];

  // Clear every channel up front — consume is single-use by contract.
  localStorage.removeItem(KEY);
  LEGACY_KEYS.forEach((k) => localStorage.removeItem(k));

  const dest = candidates.find(isSafeInternalPath);
  return dest ?? fallback;
};
